/**
 * Shared platform symbol catalog + sprite runtime for framework page scripts.
 * Mirrors source/platform/client/platform-symbols/.
 */
(function (w) {
    'use strict';

    w.Heliont = w.Heliont || {};

    const RUNTIME_ASSETS_URL = '/runtime-assets';
    const MANIFEST_URL = `${RUNTIME_ASSETS_URL}/platform-symbols/manifest.json`;
    const SPRITE_HOST_ID = 'heliont-platform-symbol-sprite';
    const TOOLBAR_MASK_ICONS = {
        add: 'folder-plus',
        remove: 'trashcan',
        trashcan: 'trashcan',
        returnable: 'returnable',
        recycle: 'recycle',
        discard: 'discard',
        translate: 'translations',
        copy: 'copy',
        open: 'chevron-right',
        'beam-probe': 'health-probes',
    };

    /** @type {Promise<{ spriteUrl: string, icons: string[] }> | null} */
    let manifestPromise = null;
    let spriteLoaded = false;
    /** @type {Promise<boolean> | null} */
    let spriteLoadPromise = null;

    /**
     * @returns {Promise<{ spriteUrl: string, icons: string[], symbolCount: number, contentHash: string, builtAt: string }>}
     */
    async function fetchManifest() {
        if (!manifestPromise) {
            manifestPromise = loadManifest();
        }

        return manifestPromise;
    }

    async function loadManifest() {
        const response = await fetch(MANIFEST_URL, {
            method: 'GET',
            cache: 'default',
            headers: { Accept: 'application/json' },
        });

        if (!response.ok) {
            throw new Error(`Failed to load platform symbol manifest: ${response.status}`);
        }

        const payload = await response.json();
        const icons = Array.isArray(payload?.icons) ? payload.icons : [];

        return {
            spriteUrl: String(payload?.spriteUrl ?? '').trim(),
            icons: [...new Set(icons.map((name) => String(name).trim()).filter(Boolean))].sort((a, b) =>
                a.localeCompare(b),
            ),
            symbolCount: Number(payload?.symbolCount ?? icons.length) || icons.length,
            contentHash: String(payload?.contentHash ?? '').trim(),
            builtAt: String(payload?.builtAt ?? '').trim(),
        };
    }

    async function listIconNames() {
        const manifest = await fetchManifest();

        return manifest.icons;
    }

    /**
     * @param {unknown} query
     * @param {number} [limit]
     */
    async function filterIconNames(query, limit = 200) {
        const q = String(query ?? '').trim().toLowerCase();
        const all = await listIconNames();

        if (!q) {
            return all.slice(0, limit);
        }

        return all.filter((name) => name.toLowerCase().includes(q)).slice(0, limit);
    }

    function isSpriteLoaded() {
        return spriteLoaded;
    }

    async function ensureSprite() {
        if (spriteLoaded) {
            return true;
        }

        if (!spriteLoadPromise) {
            spriteLoadPromise = loadSprite();
        }

        return spriteLoadPromise;
    }

    async function loadSprite() {
        try {
            const manifest = await fetchManifest();
            const spriteUrl = String(manifest?.spriteUrl ?? '').trim();

            if (spriteUrl === '') {
                return false;
            }

            const spriteResponse = await fetch(spriteUrl, {
                method: 'GET',
                cache: 'default',
            });

            if (!spriteResponse.ok) {
                return false;
            }

            const svgText = await spriteResponse.text();
            let host = document.getElementById(SPRITE_HOST_ID);

            if (!(host instanceof HTMLElement)) {
                host = document.createElement('div');
                host.id = SPRITE_HOST_ID;
                host.hidden = true;
                host.setAttribute('aria-hidden', 'true');
                document.body.appendChild(host);
            }

            host.innerHTML = svgText;
            spriteLoaded = true;
            // Build toolbar masks from the already-injected <symbol> nodes so CSS
            // mask-image does not trigger a second network download of the sprite.
            applyPlatformSymbolMaskCssVars(null, TOOLBAR_MASK_ICONS, '--hpd-mask-icon-');

            return true;
        } catch {
            return false;
        }
    }

    /**
     * @param {unknown} name
     * @returns {string}
     */
    function escapeSymbolIdFragment(name) {
        const normalized = String(name ?? '').trim();

        if (normalized === '' || !/^[a-z0-9][a-z0-9_-]*$/i.test(normalized)) {
            return '';
        }

        return normalized;
    }

    /**
     * CSS mask value from an already-injected sprite <symbol>, as a data URL.
     *
     * @param {unknown} name
     * @returns {string}
     */
    function platformSymbolMaskFromInjectedSymbol(name) {
        const normalized = escapeSymbolIdFragment(name);

        if (normalized === '') {
            return '';
        }

        const host = document.getElementById(SPRITE_HOST_ID);

        if (!(host instanceof HTMLElement)) {
            return '';
        }

        const symbol = host.querySelector(`#symbol-${normalized}`);

        if (!(symbol instanceof Element)) {
            return '';
        }

        const viewBox = String(symbol.getAttribute('viewBox') ?? '0 0 24 24').trim() || '0 0 24 24';
        const inner = String(symbol.innerHTML ?? '').trim();

        if (inner === '') {
            return '';
        }

        const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}">${inner}</svg>`;

        return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
    }

    /**
     * @param {unknown} spriteUrl
     * @param {unknown} name
     */
    function platformSymbolMaskUrl(spriteUrl, name) {
        const url = String(spriteUrl ?? '').trim();
        const normalized = String(name ?? '').trim();

        if (url === '' || normalized === '') {
            return '';
        }

        return `url("${url}#symbol-${normalized}")`;
    }

    /**
     * Prefer masks from the injected sprite host (no network). Fall back to a
     * remote sprite URL only when the host is unavailable.
     *
     * Variables are published through a dedicated <style> sheet scoped to
     * body — never written onto document.documentElement.style (that stuffed
     * giant data:image/svg+xml URLs into the <html> style attribute).
     *
     * @param {unknown} spriteUrl
     * @param {Record<string, string>} classToSymbolName
     * @param {string} [cssVarPrefix]
     */
    function applyPlatformSymbolMaskCssVars(spriteUrl, classToSymbolName, cssVarPrefix = '--hpd-mask-icon-') {
        const declarations = [];

        for (const [classSuffix, symbolName] of Object.entries(classToSymbolName)) {
            let mask = platformSymbolMaskFromInjectedSymbol(symbolName);

            if (mask === '') {
                mask = platformSymbolMaskUrl(spriteUrl, symbolName);
            }

            if (mask !== '') {
                declarations.push(`  ${cssVarPrefix}${classSuffix}: ${mask};`);
            }
        }

        if (declarations.length === 0) {
            return;
        }

        const styleId = 'heliont-platform-symbol-mask-vars';
        let styleEl = document.getElementById(styleId);
        if (!(styleEl instanceof HTMLStyleElement)) {
            styleEl = document.createElement('style');
            styleEl.id = styleId;
            document.head.appendChild(styleEl);
        }

        styleEl.textContent = `body {\n${declarations.join('\n')}\n}\n`;

        /* Clear any legacy inline vars previously dumped onto <html>. */
        const root = document.documentElement;
        for (const classSuffix of Object.keys(classToSymbolName)) {
            root.style.removeProperty(`${cssVarPrefix}${classSuffix}`);
        }
    }

    /**
     * @param {unknown} name
     */
    function symbolHref(name) {
        const normalized = String(name ?? '').trim();

        return normalized === '' ? '' : `#symbol-${normalized}`;
    }

    /**
     * @param {unknown} name
     * @param {{ className?: string, width?: number|string, height?: number|string, fill?: string, title?: string }} [options]
     */
    function renderSymbolSvg(name, options = {}) {
        const normalized = String(name ?? '').trim();

        if (normalized === '') {
            return '';
        }

        const href = symbolHref(normalized);
        const className = String(options.className ?? '').trim();
        const width = options.width;
        const height = options.height;
        const fill = options.fill;
        const title = options.title;
        const attrs = [];

        if (className !== '') {
            attrs.push(`class="${className}"`);
        }

        if (width !== undefined) {
            attrs.push(`width="${String(width)}"`);
        }

        if (height !== undefined) {
            attrs.push(`height="${String(height)}"`);
        }

        if (fill !== undefined) {
            attrs.push(`fill="${String(fill)}"`);
        }

        if (title !== undefined && String(title).trim() !== '') {
            attrs.push(`title="${String(title)}"`);
        }

        attrs.push('aria-hidden="true"');

        return `<svg ${attrs.join(' ')}><use href="${href}" /></svg>`;
    }

    w.Heliont.PlatformSymbols = {
        MANIFEST_URL,
        SPRITE_HOST_ID,
        fetchManifest,
        listIconNames,
        filterIconNames,
        ensureSprite,
        isSpriteLoaded,
        symbolHref,
        platformSymbolMaskUrl,
        platformSymbolMaskFromInjectedSymbol,
        applyPlatformSymbolMaskCssVars,
        renderSymbolSvg,
    };

    // Every page that ships this shared module gets the compiled catalog.
    // Collectors stay for PHP first-paint chrome; they are not the source of truth
    // for authored, settings, or service-supplied symbols.
    ensureSprite();
})(window);
